import type { IProductNutrition } from '@/components/ProductNutrition';
import type { Product } from '@/types/Product';
import { HTMLService } from '@/service/html/htmlService';

interface ProductDetailSchemaProps {
  product?: Product;
  nutritionData?: IProductNutrition;
  currentUrl: string;
}

export const ProductDetailSchema: React.FC<ProductDetailSchemaProps> = ({
  product,
  nutritionData,
  currentUrl,
}) => {
  if (!product) return null;

  const nutrients = nutritionData?.body?.nutrients || [];
  const energy = nutritionData?.body?.energy || null;
  const nutriensRecord = nutrients.reduce(
    (acc, nutrient) => {
      acc[nutrient.key] =
        nutrient.unPrepared?.perMeasure + ' ' + nutrient.unPrepared?.perMeasureUom;
      return acc;
    },
    {} as Record<string, string>,
  );

  const jsonLd = {
    '@context': 'https://schema.org/',
    '@type': 'Product',
    '@id': currentUrl,
    name: product.productTitle,
    description: product.productDescription,
    brand: {
      '@type': 'Brand',
      name: 'Dr. Oetker',
      logo: 'https://media.graphassets.com/qysPznNBTly3DBaldRq1',
    },
    manufacturer: {
      '@type': 'Organization',
      name: 'Dr. Oetker',
      logo: 'https://media.graphassets.com/qysPznNBTly3DBaldRq1',
      url: 'https://www.oetker.com',
    },
    image: [product.productImage?.desktop],
    sku: '', // MISSING Example: 'BK-APFEL-500',
    gtin13: product.gtin || '',
    category: product.categories[0]?.productSubcategoryPage?.subcategoryPageName || '',
    material: 'Lebensmittel',
    // weight: MISSING Example: { '@type': 'QuantitativeValue', value: 500,  unitCode: 'GRM', },
    additionalProperty: [
      {
        '@type': 'PropertyValue',
        name: 'Zubereitungsanleitung',
        value: HTMLService.toPlainText(product.preparationInstructions),
      },
    ],

    // isConsumableFor: MISSING Example: { '@type': 'DietarySupplement',  name: 'Vegetarisch', },

    nutrition: {
      '@type': 'NutritionInformation',
      servingSize: nutritionData?.servingDescription || '',
      calories: energy?.unPrepared.kiloCalories
        ? `${energy.unPrepared.kiloCalories.perMeasure} kcal`
        : '',
      fatContent: nutriensRecord['FAT'] || '',
      saturatedFatContent: nutriensRecord['FASAT'] || '',
      carbohydrateContent: nutriensRecord['CHOCDF'] || '',
      sugarContent: nutriensRecord['SUGAR-'] || '',
      proteinContent: nutriensRecord['PRO-'] || '',
      sodiumContent: nutriensRecord['SALTEQ'] || '',
    },
    /** 
    offers: {
      '@type': 'Offer',
      url: product.productPageURL,
      priceCurrency: 'EUR',
      price: '', // MISSING Example: '2.99',
      priceValidUntil: '', // MISSING Example: '2026-12-31',
      availability: 'https://schema.org/InStock',
      itemCondition: 'https://schema.org/NewCondition',
      seller: {
        '@type': 'Organization',
        name: 'Dr. Oetker',
        logo: 'https://media.graphassets.com/qysPznNBTly3DBaldRq1',
      },
    },
    */
    // aggregateRating: MISSING { '@type': 'AggregateRating', ratingValue: '4.7', reviewCount: '342', },
    // review:  MISSING Example: [{ '@type': 'Review', author: {  '@type': 'Person',   name: 'Anna K.',   },datePublished: '2025-02-14',reviewBody: 'Sehr einfach zuzubereiten und geschmacklich überzeugend.',reviewRating: {'@type': 'Rating',ratingValue: '5',},},],

    mainEntityOfPage: currentUrl,
  };
  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
    />
  );
};
